import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Add, Edit, Delete, Circle, LocationOn } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { 
  addValveToSite, 
  getSiteWithInstruments, 
  updateValveInSite, 
  deleteValveFromSite,
  getTagNamesByProjectId,
  getTelemetryData,
  logActivity
} from '../../api/apiService';
import { TELEMETRY_LIVE_POLL_MS } from '../../constants/telemetry';

const ValvesTab = ({ projectId, siteData }) => {
  const userRole = useSelector((state) => state.auth.user?.role);
  const currentUser = useSelector((state) => state.auth.user);
  const isAdminOrManager = userRole === 'Admin' || userRole === 'Manager';

  // State for valves data
  const [valves, setValves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [telemetryData, setTelemetryData] = useState({});
  const [isLoadingTelemetry, setIsLoadingTelemetry] = useState(false);

  // State for tag names
  const [tagNames, setTagNames] = useState([]);
  const [loadingTagNames, setLoadingTagNames] = useState(false);

  // State for dialogs
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedValve, setSelectedValve] = useState(null);
  const [formData, setFormData] = useState({
    valveDescription: '',
    valveLocation: '',
    valveType: '',
    openTagName: '',
    closeTagName: ''
  });

  // Fetch valves data when component mounts or projectId changes
  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) return;

      try {
        setLoading(true);
        const fullSiteData = await getSiteWithInstruments(projectId);
        setValves(fullSiteData.valves || []);
      } catch (error) {
        console.error('Error fetching site data:', error);
        setValves([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId]);

  // Fetch tag names from omsTelemetryLive table
  useEffect(() => {
    const fetchTagNames = async () => {
      if (!projectId) return;
      
      try {
        setLoadingTagNames(true);
        const names = await getTagNamesByProjectId(projectId);
        setTagNames(names);
      } catch (error) {
        console.error('Error fetching tag names:', error);
        setTagNames([]);
      } finally {
        setLoadingTagNames(false);
      }
    };

    fetchTagNames();
  }, [projectId]);

  // Fetch telemetry data for valves
  useEffect(() => {
    const fetchTelemetryData = async () => {
      if (!projectId || !valves.length) return;

      try {
        setIsLoadingTelemetry(true);
        const tagIds = [];
        
        // Collect all tag IDs from valves
        valves.forEach(valve => {
          if (valve.openTagName) {
            // Format tagId from tagName: PRJ1255_PLC1:DO_301
            const serverName = siteData?.serverName || 'PLC1';
            const cleanServerName = serverName.replace(`${projectId}_`, '');
            const formattedTagId = `${projectId}_${cleanServerName}:${valve.openTagName}`;
            tagIds.push(formattedTagId);
          }
          if (valve.closeTagName) {
            // Format tagId from tagName: PRJ1255_PLC1:DI_401
            const serverName = siteData?.serverName || 'PLC1';
            const cleanServerName = serverName.replace(`${projectId}_`, '');
            const formattedTagId = `${projectId}_${cleanServerName}:${valve.closeTagName}`;
            tagIds.push(formattedTagId);
          }
        });

        if (tagIds.length === 0) return;

        console.log('[ValvesTab] Fetching telemetry for tagIds:', tagIds);
        const data = await getTelemetryData(projectId, tagIds);
        
        // Create a map of tagId to value
        const telemetryMap = {};
        data.forEach(item => {
          telemetryMap[item.tagId] = {
            value: item.value,
            timestamp: item.timestamp,
            localTime: item.localTime,
            deviceLocalTime: item.deviceLocalTime
          };
        });
        
        setTelemetryData(telemetryMap);
      } catch (error) {
        console.error('[ValvesTab] Error fetching telemetry data:', error);
      } finally {
        setIsLoadingTelemetry(false);
      }
    };

    // Initial fetch
    fetchTelemetryData();

    const intervalId = setInterval(fetchTelemetryData, TELEMETRY_LIVE_POLL_MS);

    return () => clearInterval(intervalId);
  }, [projectId, valves, siteData]);

  // Handle form data change
  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = async () => {
    const newValve = {
      id: Date.now(),
      valveDescription: formData.valveDescription,
      valveLocation: formData.valveLocation,
      valveType: formData.valveType,
      openTagName: formData.openTagName,
      closeTagName: formData.closeTagName,
    };

    try {
      await addValveToSite(projectId, newValve);
      console.log('New valve added successfully!');
      
      // Log add valve activity to DynamoDB
      try {
        await logActivity(currentUser.email, `Added ${formData.valveType}: ${newValve.valveDescription}`);
      } catch (err) {
        console.error('Error logging activity to DynamoDB:', err);
      }
      
      const fullSiteData = await getSiteWithInstruments(projectId);
      setValves(fullSiteData.valves || []);
      setOpenDialog(false);
      setFormData({
        valveDescription: '',
        valveLocation: '',
        valveType: '',
        openTagName: '',
        closeTagName: ''
      });
    } catch (error) {
      console.error('Error adding valve to DynamoDB:', error);
    }
  };

  // Handle Edit
  const handleEdit = (valve) => {
    setSelectedValve(valve);
    setFormData({
      valveDescription: valve.valveDescription || '',
      valveLocation: valve.valveLocation || '',
      valveType: valve.valveType || '',
      openTagName: valve.openTagName || '',
      closeTagName: valve.closeTagName || '',
    });
    setOpenEditDialog(true);
  };

  // Handle Delete
  const handleDelete = (valve) => {
    setSelectedValve(valve);
    setOpenDeleteDialog(true);
  };

  // Handle Edit Submit
  const handleEditSubmit = async () => {
    try {
      const updatedValve = {
        ...selectedValve,
        ...formData,
      };
      
      // Ensure we're not creating a new ID
      if (!updatedValve.id) {
        updatedValve.id = selectedValve.id;
      }

      if (userRole === 'Admin' || userRole === 'Manager') {
        await updateValveInSite(projectId, updatedValve);
        // Log edit valve activity
        try {
          await logActivity(currentUser.email, `Edited ${updatedValve.valveType}: ${updatedValve.valveDescription}`);
        } catch (err) {
          console.error('Error logging edit valve activity to DynamoDB:', err);
        }
      }
      // Update local state for all roles
      const fullSiteData = await getSiteWithInstruments(projectId);
      setValves(fullSiteData.valves || []);
      
      // Close dialog and reset state
      setOpenEditDialog(false);
      setSelectedValve(null);
      setFormData({
        valveDescription: '',
        valveLocation: '',
        valveType: '',
        openTagName: '',
        closeTagName: ''
      });
    } catch (error) {
      console.error('Error updating valve:', error);
    }
  };

  // Handle Delete Confirm
  const handleDeleteConfirm = async () => {
    try {
      await deleteValveFromSite(projectId, selectedValve.id);
      // Log delete valve activity
      try {
        await logActivity(currentUser.email, `Deleted ${selectedValve.valveType}: ${selectedValve.valveDescription}`);
      } catch (err) {
        console.error('Error logging delete valve activity to DynamoDB:', err);
      }
      console.log('Valve deleted successfully!');
      const fullSiteData = await getSiteWithInstruments(projectId);
      setValves(fullSiteData.valves || []);
      setOpenDeleteDialog(false);
      setSelectedValve(null);
    } catch (error) {
      console.error('Error deleting valve:', error);
    }
  };

  // Get valve status based on telemetry data
  const getValveStatus = (valve) => {
    if (!valve.openTagName && !valve.closeTagName) {
      return 'Unknown';
    }
    
    // Format tagId from tagName for telemetry lookup
    const serverName = siteData?.serverName || 'PLC1';
    const cleanServerName = serverName.replace(`${projectId}_`, '');
    
    let openValue = null;
    let closeValue = null;
    
    if (valve.openTagName) {
      const openTagId = `${projectId}_${cleanServerName}:${valve.openTagName}`;
      if (telemetryData[openTagId]) {
        openValue = telemetryData[openTagId].value;
      }
    }
    
    if (valve.closeTagName) {
      const closeTagId = `${projectId}_${cleanServerName}:${valve.closeTagName}`;
      if (telemetryData[closeTagId]) {
        closeValue = telemetryData[closeTagId].value;
      }
    }
    
    // Determine valve status based on open and close signals
    if (openValue === '1' || openValue === 1 || openValue === 'true' || openValue === true) {
      return 'Open';
    } else if (closeValue === '1' || closeValue === 1 || closeValue === 'true' || closeValue === true) {
      return 'Closed';
    }
    
    return 'Unknown';
  };

  // Get status indicator component
  const getStatusIndicator = (status) => {
    const isOpen = status === 'Open';
    const isClosed = status === 'Closed';
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Open Indicator */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 1 
        }}>
          <Circle sx={(theme) => ({ 
            color: isOpen ? theme.palette.status.normalGreen : theme.palette.grey[300], 
            fontSize: 24,
            transition: 'color 0.3s ease'
          })} />
          <Typography variant="caption" sx={(theme) => ({ 
            color: isOpen ? theme.palette.status.normalGreen : theme.palette.grey[500], 
            fontWeight: isOpen ? 600 : 400,
            fontSize: '0.75rem',
            transition: 'all 0.3s ease'
          })}>
            OPEN
          </Typography>
        </Box>
        
        {/* Closed Indicator */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 1 
        }}>
          <Circle sx={(theme) => ({ 
            color: isClosed ? theme.palette.status.alarmRed : theme.palette.grey[300], 
            fontSize: 24,
            transition: 'color 0.3s ease'
          })} />
          <Typography variant="caption" sx={(theme) => ({ 
            color: isClosed ? theme.palette.status.alarmRed : theme.palette.grey[500], 
            fontWeight: isClosed ? 600 : 400,
            fontSize: '0.75rem',
            transition: 'all 0.3s ease'
          })}>
            CLOSED
          </Typography>
        </Box>
      </Box>
    );
  };


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress sx={{ color: (theme) => theme.palette.primary.main }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: { xs: '300px', sm: '350px', md: '400px' },
      overflow: 'auto',
      position: 'relative'
    }}>
      <Stack spacing={2}>
        {valves.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '200px',
            color: (theme) => theme.palette.text.secondary
          }}>
            <Typography variant="body1">No valves found. Add your first valve using the + button.</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            {valves.map((valve, index) => {
              const status = getValveStatus(valve);
              
              return (
                <Card key={valve.id || index} sx={{ 
                  borderRadius: '16px', 
                  boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 6px 18px rgba(0,0,0,0.35)' : '0 4px 12px rgba(0,0,0,0.06)',
                  background: (theme) => theme.palette.mode === 'dark' ? theme.palette.background.paper : '#FFFFFF',
                  border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
                  transition: 'box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 10px 28px rgba(0,0,0,0.45)' : '0 8px 24px rgba(0,0,0,0.10)',
                    borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)',
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 700, 
                        fontSize: '1.1rem',
                        color: (theme) => theme.palette.text.primary,
                        lineHeight: 1.3
                      }}>
                        {valve.valveDescription}
                      </Typography>
                      {isAdminOrManager && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Edit Valve">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(valve);
                              }}
                              sx={{
                                color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                                '&:hover': { backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.10) },
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Valve">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(valve);
                              }}
                              sx={{
                                color: (theme) => theme.palette.error.main,
                                '&:hover': { backgroundColor: (theme) => alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.16 : 0.10) },
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                    <Stack spacing={2}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        p: 1.5,
                        backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.06),
                        borderRadius: '8px'
                      }}>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 600, 
                          color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                          fontSize: '0.875rem'
                        }}>
                          {valve.valveType}
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        p: 1.5,
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : alpha(theme.palette.text.primary, 0.03),
                        borderRadius: '8px'
                      }}>
                        <Typography variant="body2" sx={{ 
                          color: (theme) => theme.palette.text.secondary,
                          fontSize: '0.875rem',
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          <LocationOn sx={{ fontSize: 16, mr: 0.5, color: (theme) => theme.palette.text.secondary }} /> {valve.valveLocation}
                        </Typography>
                      </Box>
                      
                      {/* Status Indicators */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        p: 2,
                        backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.15 : 0.08),
                        borderRadius: '8px'
                      }}>
                        {isLoadingTelemetry ? (
                          <CircularProgress size={20} sx={{ color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main }} />
                        ) : (
                          getStatusIndicator(status)
                        )}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Stack>
      
      {isAdminOrManager && (
        <Fab
          color="primary"
          aria-label="add valve"
          onClick={() => setOpenDialog(true)}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000,
            backgroundColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
            color: (theme) => theme.palette.getContrastText(theme.palette.brand?.royalBlue || theme.palette.primary.main),
            '&:hover': {
              backgroundColor: (theme) => theme.palette.mode === 'dark' 
                ? alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, 0.85)
                : theme.palette.brand?.royalBlue || theme.palette.primary.main,
              filter: (theme) => theme.palette.mode === 'dark' ? 'none' : 'brightness(0.95)',
            },
          }}
        >
          <Add />
        </Fab>
      )}

      {/* Add Valve Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: "12px", 
            padding: {
              xs: "12px",
              sm: "16px",
              md: "20px"
            },
            boxShadow: "0px 8px 24px rgba(0,0,0,0.15)"
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: "600", 
          color: "#1A1F36",
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontSize: {
            xs: '1.1rem',
            sm: '1.25rem',
            md: '1.5rem'
          }
        }}>
          Add New Valve
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ marginTop: "16px" }}>
            <TextField
              fullWidth
              label="Valve Description"
              name="valveDescription"
              value={formData.valveDescription}
              onChange={handleFormChange}
              variant="outlined"
              required
            />
            <TextField
              fullWidth
              label="Valve Location"
              name="valveLocation"
              value={formData.valveLocation}
              onChange={handleFormChange}
              variant="outlined"
              required
            />
            <FormControl fullWidth>
              <InputLabel id="valve-type-label">Valve Type</InputLabel>
              <Select
                labelId="valve-type-label"
                label="Valve Type"
                name="valveType"
                value={formData.valveType}
                onChange={handleFormChange}
                variant="outlined"
                required
              >
                <MenuItem value="">Select Valve Type</MenuItem>
                <MenuItem value="Inlet Valve">Inlet Valve</MenuItem>
                <MenuItem value="Outlet Valve">Outlet Valve</MenuItem>
                <MenuItem value="Bypass Valve">Bypass Valve</MenuItem>
                <MenuItem value="Drain Valve">Drain Valve</MenuItem>
                <MenuItem value="Pressure Relief Valve">Pressure Relief Valve</MenuItem>
                <MenuItem value="Check Valve">Check Valve</MenuItem>
                <MenuItem value="Flow Control Valve">Flow Control Valve</MenuItem>
                <MenuItem value="Isolation Valve">Isolation Valve</MenuItem>
                <MenuItem value="Control Valve">Control Valve</MenuItem>
                <MenuItem value="Safety Valve">Safety Valve</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="open-tag-label">Open Tag Name</InputLabel>
              <Select
                labelId="open-tag-label"
                label="Open Tag Name"
                name="openTagName"
                value={formData.openTagName}
                onChange={handleFormChange}
                variant="outlined"
                disabled={loadingTagNames}
                required
              >
                <MenuItem value="">Select an Open Tag Name</MenuItem>
                {tagNames.map((tagName) => (
                  <MenuItem key={tagName} value={tagName}>
                    {tagName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="close-tag-label">Close Tag Name</InputLabel>
              <Select
                labelId="close-tag-label"
                label="Close Tag Name"
                name="closeTagName"
                value={formData.closeTagName}
                onChange={handleFormChange}
                variant="outlined"
                disabled={loadingTagNames}
                required
              >
                <MenuItem value="">Select a Close Tag Name</MenuItem>
                {tagNames.map((tagName) => (
                  <MenuItem key={tagName} value={tagName}>
                    {tagName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ padding: "16px" }}>
          <Button 
            onClick={() => setOpenDialog(false)} 
            color="secondary" 
            sx={{ 
              textTransform: "none",
              color: '#666',
              '&:hover': {
                backgroundColor: '#F5F5F5',
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary" 
            sx={{ 
              textTransform: "none", 
              boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
              backgroundColor: '#1976D2',
              '&:hover': {
                backgroundColor: '#1565C0',
              }
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Valve Dialog */}
      <Dialog 
        open={openEditDialog} 
        onClose={() => setOpenEditDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: '600', color: '#1A1F36' }}>Edit Valve</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ marginTop: '16px' }}>
            <TextField
              fullWidth
              label="Valve Description"
              name="valveDescription"
              value={formData.valveDescription}
              onChange={handleFormChange}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Valve Location"
              name="valveLocation"
              value={formData.valveLocation}
              onChange={handleFormChange}
              variant="outlined"
            />
            <FormControl fullWidth>
              <InputLabel id="edit-valve-type-label">Valve Type</InputLabel>
              <Select
                labelId="edit-valve-type-label"
                label="Valve Type"
                name="valveType"
                value={formData.valveType}
                onChange={handleFormChange}
                variant="outlined"
              >
                <MenuItem value="">Select Valve Type</MenuItem>
                <MenuItem value="Inlet Valve">Inlet Valve</MenuItem>
                <MenuItem value="Outlet Valve">Outlet Valve</MenuItem>
                <MenuItem value="Bypass Valve">Bypass Valve</MenuItem>
                <MenuItem value="Drain Valve">Drain Valve</MenuItem>
                <MenuItem value="Pressure Relief Valve">Pressure Relief Valve</MenuItem>
                <MenuItem value="Check Valve">Check Valve</MenuItem>
                <MenuItem value="Flow Control Valve">Flow Control Valve</MenuItem>
                <MenuItem value="Isolation Valve">Isolation Valve</MenuItem>
                <MenuItem value="Control Valve">Control Valve</MenuItem>
                <MenuItem value="Safety Valve">Safety Valve</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="edit-open-tag-label">Open Tag Name</InputLabel>
              <Select
                labelId="edit-open-tag-label"
                label="Open Tag Name"
                name="openTagName"
                value={formData.openTagName}
                onChange={handleFormChange}
                variant="outlined"
                disabled={loadingTagNames}
              >
                <MenuItem value="">Select an Open Tag Name</MenuItem>
                {tagNames.map((tagName) => (
                  <MenuItem key={tagName} value={tagName}>
                    {tagName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="edit-close-tag-label">Close Tag Name</InputLabel>
              <Select
                labelId="edit-close-tag-label"
                label="Close Tag Name"
                name="closeTagName"
                value={formData.closeTagName}
                onChange={handleFormChange}
                variant="outlined"
                disabled={loadingTagNames}
              >
                <MenuItem value="">Select a Close Tag Name</MenuItem>
                {tagNames.map((tagName) => (
                  <MenuItem key={tagName} value={tagName}>
                    {tagName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleEditSubmit} 
            variant="contained" 
            color="primary"
          >
            Submit
          </Button>
          <Button 
            onClick={() => setOpenEditDialog(false)} 
            color="secondary"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Valve Dialog */}
      <Dialog 
        open={openDeleteDialog} 
        onClose={() => setOpenDeleteDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: '600', color: '#1A1F36' }}>Delete Valve</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this valve?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained" 
            color="primary"
          >
            Delete
          </Button>
          <Button 
            onClick={() => setOpenDeleteDialog(false)} 
            color="secondary"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ValvesTab; 